import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import PublicShell from '../components/PublicShell'
import Spinner from '../components/Spinner'
import { coursePath } from '../components/courses/courseUtils'
import { coursesAPI } from '../services/api'
import type { CertificateCheck } from '../types'

type State = { kind: 'loading' } | { kind: 'missing' } | { kind: 'ready'; cert: CertificateCheck }

/**
 * A course certificate, checkable by anyone with its number
 * (/learn/certificates/<number>). It says what it is: a record that every
 * lesson was completed, never a qualification or licence.
 */
const CertificatePage = () => {
  const { number } = useParams()
  const [state, setState] = useState<State>({ kind: 'loading' })

  useEffect(() => {
    if (!number) return setState({ kind: 'missing' })
    coursesAPI
      .verifyCertificate(number)
      .then((cert) => setState({ kind: 'ready', cert }))
      .catch(() => setState({ kind: 'missing' }))
  }, [number])

  return (
    <PublicShell>
      {state.kind === 'loading' && (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      )}
      {state.kind === 'missing' && (
        <div className="py-16 text-center" data-testid="certificate-missing">
          <h1 className="text-xl font-semibold text-gray-100">No certificate has that number</h1>
          <p className="mt-2 text-sm text-gray-400">Check it against the one you were given.</p>
        </div>
      )}
      {state.kind === 'ready' && (
        <article
          className="mx-auto max-w-xl rounded-xl border border-terminal-border bg-terminal-surface p-8 text-center"
          data-testid="certificate"
        >
          <p className="text-xs uppercase tracking-wider text-gray-500">Certificate of completion</p>
          <h1 className="mt-3 text-2xl font-bold text-gray-50">
            {state.cert.student_username ? `@${state.cert.student_username}` : 'A former member'}
          </h1>
          <p className="mt-2 text-gray-300">
            completed{' '}
            <Link to={coursePath(state.cert.course_slug)} className="font-medium text-sky-300 underline">
              {state.cert.course_title}
            </Link>
            {state.cert.instructor_username ? ` by @${state.cert.instructor_username}` : ''}
          </p>
          <p className="mt-1 text-sm text-gray-400">on {new Date(state.cert.completion_date).toLocaleDateString()}</p>
          <p
            className={`mt-4 text-sm font-medium ${state.cert.valid ? 'text-success-500' : 'text-amber-300'}`}
            data-testid="certificate-valid"
          >
            {state.cert.valid ? 'Valid' : 'This certificate has been revoked'}
          </p>
          <p className="mt-4 text-xs text-gray-500">{state.cert.statement}</p>
          <p className="mt-1 font-mono text-xs text-gray-500">{state.cert.certificate_number}</p>
        </article>
      )}
    </PublicShell>
  )
}

export default CertificatePage
